import * as React from 'react';
import { styled } from 'styled-components';
import { SemanticTextColor } from '@foradevelopment/design-tokens';

import { theme } from '../../styles';
import { createResponsiveGeneralStyle, createResponsiveSpacingStyle, getSemanticTextColor } from '../../utils';
import type { Colors, ResponsiveSpacing } from '../../utils/types';

type TextProps = {
  style?: object;
  className?: string;
  testId?: string;
  renderAs?: 'span' | 'p';
  marginTop?: ResponsiveSpacing;
  marginBottom?: ResponsiveSpacing;
  marginLeft?: ResponsiveSpacing;
  marginRight?: ResponsiveSpacing;
  width?: ResponsiveSpacing;
  color?: Colors | SemanticTextColor;
  size?: 'xxs' | 'xs' | 'small' | 'standard' | 'medium' | 'large';
  fontWeight?: 'light' | 'normal' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textAlign?: 'left' | 'center' | 'right' | ResponsiveSpacing;
  textDecoration?: 'none' | 'underline' | 'line-through';
  whiteSpace?: 'normal' | 'pre' | 'nowrap' | 'pre-wrap' | 'break-spaces' | 'pre-line';
  ariaHidden?: boolean;
  children: React.ReactNode;
};

const fontWeightToNumber = (fontWeight: string): number =>
  fontWeight === 'bold' ? 700 : fontWeight === 'semibold' ? 600 : fontWeight === 'light' ? 400 : 500;

const Span = styled.span<TextProps>`
  ${props => props.size === 'xxs' && theme.typography.textXXSmall};
  ${props => props.size === 'xs' && theme.typography.textXSmall};
  ${props => props.size === 'small' && theme.typography.textSmall};
  ${props => props.size === 'standard' && theme.typography.textStandard};
  ${props => props.size === 'medium' && theme.typography.textMedium};
  ${props => props.size === 'large' && theme.typography.textLarge};
  color: ${props => getSemanticTextColor(props.color || 'default')};
  ${props => props.fontWeight && `font-weight: ${fontWeightToNumber(props.fontWeight)}`};
  ${props => props.fontStyle && `font-style: ${props.fontStyle}`};
  ${props => props.textDecoration && `text-decoration: ${props.textDecoration}`};
`;

const StyledText = styled(Span)<TextProps>`
  margin-top: 0;
  margin-bottom: 0;
  ${props => props.marginTop && createResponsiveSpacingStyle('margin-top', props.marginTop)};
  ${props => props.marginBottom && createResponsiveSpacingStyle('margin-bottom', props.marginBottom)};
  ${props => props.marginLeft && createResponsiveSpacingStyle('margin-left', props.marginLeft)};
  ${props => props.marginRight && createResponsiveSpacingStyle('margin-right', props.marginRight)};
  ${props => props.width && createResponsiveSpacingStyle('width', props.width)};
  ${props => props.textAlign && createResponsiveGeneralStyle('text-align', props.textAlign)};
  ${props => props.whiteSpace && createResponsiveGeneralStyle('white-space', props.whiteSpace)};
`;

export const Text = ({
  style,
  className,
  testId,
  renderAs = 'p',
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  size = 'standard',
  color,
  width = 'auto',
  fontWeight = 'normal',
  fontStyle = 'normal',
  textAlign,
  textDecoration = 'none',
  whiteSpace,
  ariaHidden = false,
  children,
}: TextProps) => (
  <StyledText
    style={style}
    className={className}
    as={renderAs}
    data-testid={testId}
    size={size}
    color={color}
    marginTop={marginTop}
    marginBottom={marginBottom}
    marginLeft={marginLeft}
    marginRight={marginRight}
    width={width}
    fontWeight={fontWeight}
    fontStyle={fontStyle}
    textAlign={textAlign}
    textDecoration={textDecoration}
    whiteSpace={whiteSpace}
    aria-hidden={ariaHidden}
  >
    {children}
  </StyledText>
);
